import { useState } from 'react'
import {
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { DocumentMeta } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { saveDocumentFile } from '../../api/download'
import { SectionCard } from '../../components/SectionCard'
import { StatusChip } from '../../components/StatusChip'

interface ClientDocumentsCardProps {
  title: string
  documents: DocumentMeta[]
  /** Id dokumentů z posledního generování — zvýrazní se. */
  highlightIds?: string[]
}

/** Seznam vygenerovaných dokumentů klienta: název, stav, datum, stažení. */
export function ClientDocumentsCard({ title, documents, highlightIds }: ClientDocumentsCardProps) {
  const api = useOpsApi()
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleDownload = async (documentId: string) => {
    setDownloadingId(documentId)
    setError(null)
    try {
      saveDocumentFile(await api.downloadDocument(documentId))
    } catch (err) {
      setError(apiError(err, 'Stažení dokumentu selhalo'))
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <SectionCard title={title}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Klient zatím nemá žádné dokumenty.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Dokument</TableCell>
              <TableCell>Stav</TableCell>
              <TableCell>Vytvořeno</TableCell>
              <TableCell align="right">Stáhnout</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((document) => {
              const highlighted = highlightIds?.includes(document.id)
              return (
                <TableRow
                  key={document.id}
                  sx={highlighted ? { bgcolor: 'rgba(10,30,60,0.04)' } : undefined}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: highlighted ? 600 : 400 }}>
                      {document.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {document.fileName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={document.state} />
                  </TableCell>
                  <TableCell>
                    {new Date(document.createdAt).toLocaleDateString('cs-CZ')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Stáhnout PDF">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(document.id)}
                          disabled={downloadingId === document.id}
                        >
                          {downloadingId === document.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DownloadIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  )
}
